import React, { useState, useEffect } from 'react';

import YamhaBike from '../resource/kgv4.png';
import { Link } from 'react-router-dom';
import backgroundView from '../resource/backgroundView.jpg';
import group from '../resource/group.png';
import kgvmitra from '../resource/kgvmitra1.png';
import mountain from '../resource/mountain.png';
import Vector from '../resource/Vector.png';
import YamhaBike1 from '../resource/YamhaBike1.png';
import AvgBike1 from '../resource/AvgBike1.png';
import EnergyFrame from '../resource/EnergyFrame.png';
import BikeVideo from '../resource/hii.mp4';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Zap, Battery, Gauge, Repeat, Cable, Sparkles } from 'lucide-react';
import {
	faInstagram,
	faTwitter,
	faLinkedinIn,
} from '@fortawesome/free-brands-svg-icons';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import ZeroHundred from '../components/ZeroHundred';
import HelmetPopup from '../components/HelmetPopup';

const ComponentCard = ({ icon: Icon, heading, content }) => (
	<div className="group relative hover:bg-gradient-to-tl from-green-50 to-green-100 shadow-md hover:scale-105 rounded-xl p-6 border border-gray-200 hover:border-green-500 transition-all duration-300 ease-out hover:shadow-lg">
		<div className="flex flex-col items-center text-center space-y-4">
			<div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center group-hover:bg-green-500 transition-colors duration-300">
				<Icon className="w-8 h-8 text-green-600 group-hover:text-white transition-colors duration-300" />
			</div>
			<h3 className="text-lg font-bold text-green-900">{heading}</h3>
			<p className="text-sm text-gray-600 leading-relaxed">{content}</p>
		</div>
	</div>
);

const components = [
	{
		icon: Zap,
		heading: 'KGV SMART MOTOR',
		content:
			'An AI-optimized electric motor integrated directly into the wheel hub, providing intelligent power delivery. Designed for smart e-mobility, it enables seamless drive systems with minimal mechanical complexity.',
	},
	{
		icon: Battery,
		heading: 'KGV E-POWER HOUSE',
		content:
			'Advanced AI-driven power management system controlling motor output, digital metrics, and accessories. Single-phase design with 1-2.5KW power range, ensuring optimal hybrid vehicle performance through predictive analytics.',
	},
	{
		icon: Gauge,
		heading: 'DUAL FUEL THROTTLE',
		content:
			'Hall effect magnetic sensor-based throttle system with AI-assisted speed control. Features intuitive switches (Low-Medium-High) for enhanced driving flexibility and energy efficiency.',
	},
	{
		icon: Repeat,
		heading: 'DC/DC CONVERTER',
		content:
			'Electromechanical device converting DC power between voltage levels. Efficiently steps down high voltage for auxiliary systems, now enhanced with AI for superior heat management and reliability.',
	},
	{
		icon: Cable,
		heading: 'WIRING HARNESS',
		content:
			'Complete AI-ready wiring solution with precision-engineered connectors, reinforced cables, and battery mounting accessories. Ensures reliable electrical connections throughout the intelligent system.',
	},
];

export function Home() {
	const [isVisible, setIsVisible] = useState(false);
	const [currentBike, setCurrentBike] = useState(0);
	const [displayedText, setDisplayedText] = useState('');

	const [showPromoPopup, setShowPromoPopup] = useState(true);

	const headingText = "World's First Smart E-Mobility Solution";

	const handlePromoClose = () => {
		setShowPromoPopup(false);
	};

	useEffect(() => {
		// Animation for the bike image
		setIsVisible(true);

		// Bike image swap animation
		const interval = setInterval(() => {
			setCurrentBike((prev) => (prev === 0 ? 1 : 0));
		}, 5000);

		return () => clearInterval(interval);
	}, []);

	useEffect(() => {
		// Typewriter effect - show letter by letter
		let currentIndex = 0;
		const timer = setInterval(() => {
			if (currentIndex <= headingText.length) {
				setDisplayedText(headingText.substring(0, currentIndex));
				currentIndex++;
			} else {
				clearInterval(timer);
			}
		}, 100); // 100ms delay between each letter

		return () => clearInterval(timer);
	}, []);

	return (
		<main className="w-full min-h-screen bg-white font-sans overflow-x-hidden">
			<HelmetPopup isOpen={showPromoPopup} onClose={handlePromoClose} />
			<style>{`
        @keyframes wordFade {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-word {
          animation: wordFade 0.6s ease forwards;
        }
        @keyframes shimmerSweep {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(100%); }
          100% { transform: translateX(100%); }
        }
        .shimmer-sweep {
          animation: shimmerSweep 3s infinite linear;
        }
      `}</style>

			{/* Hero Section */}
			<section className="relative w-full min-h-[60vh] py-8 md:py-12 lg:py-20 bg-white flex flex-col items-center justify-center text-center overflow-hidden">
				<div
					className="absolute inset-0 bg-cover bg-center z-0"
					style={{
						backgroundImage: `url(${backgroundView})`,
						opacity: 0.3,
					}}
				></div>

				<h1
					className="
          text-center relative 
          pb-2 
          mb-4 sm:mb-6 md:mb-8
          font-playfair text-green-900 font-bold 
          text-2xl sm:text-3xl md:text-4xl lg:text-4xl 
          uppercase tracking-wide z-10 px-4
          min-h-[1.5em]
        "
				>
					{displayedText}
					<span className="animate-pulse">|</span>
				</h1>

				{/* Premium Powered by AI Badge */}
				<div className="z-10 mb-8 group transition-all duration-500 hover:scale-110 active:scale-95">
					<span className="relative overflow-hidden bg-black/90 backdrop-blur-xl px-5 py-2.5 rounded-full shadow-[0_0_20px_rgba(34,197,94,0.4)] flex items-center gap-3 border border-green-500/50">
						{/* Shimmer Light Effect */}
						<span className="absolute inset-0 w-1/2 h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -rotate-45 shimmer-sweep"></span>

						<Sparkles className="w-4 h-4 text-green-400 animate-pulse" />
						<span className="text-white text-[10px] sm:text-xs font-black tracking-[0.2em] uppercase">
							POWERED BY <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">AI INTELLIGENCE</span>
						</span>
					</span>
				</div>

				<div className="relative z-10 px-4 md:px-8 max-w-7xl w-full">
					<div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12">
						{/* Left Content */}
						<div className="relative w-full lg:w-1/2 flex flex-col items-center lg:items-start">
							<img
								src={group}
								alt="Background Blob"
								className="absolute hidden lg:block top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full object-contain z-0 opacity-90"
							/>

							<div className="relative bg-green-900 p-6 md:p-8 lg:p-10 rounded-2xl lg:rounded-3xl w-full max-w-2xl z-10">
								<p className="text-white/90 text-sm sm:text-base md:text-lg leading-relaxed">
									At KGV HYBRID GREEN MOBILITY PRIVATE LIMITED, we are revolutionizing the
									future of transportation. Our cutting-edge Smart E-Mobility
									Solution enables you to convert your conventional petrol bike
									into a hybrid bike that runs seamlessly on both petrol and
									battery power. This innovation combines the best of both
									worlds—fossil fuel reliability with the efficiency and
									eco-friendliness of electric power.
								</p>

								{/* Mobile-only short version */}
								<p className="text-white/90 text-sm leading-relaxed mt-4 lg:hidden">
									Convert your petrol bike to hybrid. Enjoy dual fuel efficiency
									with zero range anxiety.
								</p>
							</div>
						</div>

						{/* Right Image */}
						<div
							className={`relative w-full lg:w-1/2 transition-all duration-1000 ease-out will-change-transform will-change-opacity ${isVisible
								? 'translate-x-0 opacity-100 scale-100 blur-0'
								: 'translate-x-16 opacity-0 scale-95 blur-sm'
								}
  `}
						>
							<div className="relative">
								<div className="absolute inset-0 bg-gradient-to-br from-green-400/20 to-emerald-500/20 rounded-3xl rotate-3"></div>
								<div className="absolute inset-0 bg-gradient-to-tl from-green-500/20 to-emerald-400/20 rounded-3xl -rotate-3"></div>

								<img
									src={YamhaBike}
									alt="Hybrid Bike"
									className="relative z-10 object-contain w-full h-auto transition-transform duration-500 ease-out hover:scale-125 drop-shadow-2xl"
								/>
							</div>
						</div>
					</div>
				</div>

				{/* Social Media Links */}
				<div className="w-full flex justify-center lg:justify-end px-4 lg:pr-8 z-10 mt-6">
					<div className="flex gap-4">

						<div className="flex items-center bg-green-100 hover:scale-110 justify-center w-12 h-12 rounded-full transition duration-500 hover:bg-green-700 text-green-700 hover:text-white">
							<a
								href="https://www.instagram.com/kgvllp?igsh=MWZwbnZxbG4xZnZz"
								className=" text-2xl sm:text-3xl rounded-full"
								target="_blank"
								rel="noopener noreferrer"
								aria-label="Instagram"
							>
								<FontAwesomeIcon icon={faInstagram} />
							</a>
						</div>
						<div className="flex items-center bg-green-100 hover:scale-110 justify-center w-12 h-12 rounded-full transition duration-500 hover:bg-green-700 text-green-700 hover:text-white">
							<a
								href="https://x.com/KGVllp?t=ecvFy4j65dHiRpRzpOWGtg&s=09"
								className=" text-2xl sm:text-3xl rounded-full"
								target="_blank"
								rel="noopener noreferrer"
								aria-label="Twitter"
							>
								<FontAwesomeIcon icon={faTwitter} />
							</a>
						</div>
						<div className="flex items-center bg-green-100 hover:scale-110 justify-center w-12 h-12 rounded-full transition duration-500 hover:bg-green-700 text-green-700 hover:text-white">
							<a
								href="https://www.linkedin.com/company/karishmaglobal/"
								className=" text-2xl sm:text-3xl rounded-full"
								target="_blank"
								rel="noopener noreferrer"
								aria-label="Linkedin"
							>
								<FontAwesomeIcon icon={faLinkedinIn} />
							</a>
						</div>

					</div>
				</div>
			</section>

			{/* Bottom Section */}
			<section
				className="bg-green-700 text-white px-4 sm:px-6 md:px-8 flex flex-col lg:flex-row items-center justify-between gap-6"
				style={{
					backgroundImage: `url(${mountain})`,
					backgroundSize: 'cover',
					backgroundPosition: 'center 80%',
				}}
			>
				<p className="text-base sm:text-lg md:text-xl max-w-4xl text-center lg:text-left lg:ml-12">
					KGV HYBRID GREEN MOBILITY PRIVATE LIMITED, we are revolutionizing the future of
					transportation. Our cutting-edge Smart E-Mobility Solution enables you
					to convert your conventional petrol bike into a hybrid Bike that runs
					seamlessly on both petrol and battery power. This innovation combines
					the best of both worlds-fossil fuel reliability with the efficiency
					and eco-friendliness of electric power.
				</p>
				<img
					src={kgvmitra}
					alt="KGV Mascot"
					className="w-48 sm:w-64 md:w-72 lg:w-80 h-auto lg:mr-16"
				/>
			</section>

			{/* Products Section */}
			<div className="w-full bg-white rounded-xl shadow-2xl overflow-hidden">
				<h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold font-playfair text-green-950 text-center my-6 px-4">
					Our <span className="text-green-600">Products.</span>
				</h2>

				<div className="relative w-full h-[60vh] sm:h-[70vh] md:h-[80vh] lg:h-[90vh] flex flex-col items-center justify-center bg-[#F6F8FC] overflow-hidden">
					{/* Know More Button */}
					{/* <Link to={"/KgvkitOrder"}>
    <button
      className="
        absolute top-4 sm:top-8 md:top-12 lg:top-16 left-1/2 -translate-x-1/2 
        flex items-center justify-center gap-2 
        bg-red-600 hover:bg-red-700 active:scale-95 
        transition-all duration-200 
        text-white 
        text-xs sm:text-sm md:text-base lg:text-lg 
        font-bold 
        px-4 py-2 sm:px-5 sm:py-2.5 md:px-6 md:py-3 
        rounded-xl 
        shadow-lg shadow-red-300/40
        z-10
      "
    >
      <span className="whitespace-nowrap">Know More</span>
      <img src={Vector} alt="Vector" className="h-3 w-3 sm:h-4 sm:w-4" />
    </button>
  </Link> */}

					{/* Video Section */}
					<video
						src={BikeVideo}
						autoPlay
						loop
						muted
						playsInline
						className="w-400px h-full object-contain sm:object-cover"
					/>
				</div>
			</div>

			{/* Components Section */}
			<div className="bg-gradient-to-b  from-white to-gray-50 rounded-2xl shadow-2xl p-6 sm:p-8 max-w-6xl mx-auto my-8">
				<div className="text-center mb-8">
					<h2 className="text-xl md:text-4xl font-extrabold font-inter text-center text-green-700 mb-3">
						Kit Components
					</h2>
					<p className="text-md md:text-lg text-green-800 mt-3 mb-8 text-center leading-relaxed font-inter p-4 bg-green-100 rounded-full w-2/3 mx-auto">
						Premium quality components engineered for reliability and
						performance
					</p>
				</div>

				<div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
					{components.map((component, index) => (
						<ComponentCard
							key={index}
							icon={component.icon}
							heading={component.heading}
							content={component.content}
						/>
					))}
				</div>
			</div>

			{/* Pricing Section */}
			<div className="py-8 px-4 sm:px-6 lg:px-8">
				<div className="bg-white rounded-2xl max-w-[1332px] shadow-2xl p-6 sm:p-8 lg:p-12 mx-auto border border-green-100">
					<h2 className="text-xl md:text-4xl font-extrabold font-inter text-center">
						<span className="text-green-700">
							Green Drive KGV E-Hybrid Bike{' '}
						</span>
						<span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
							@14 Paise Per KM
						</span>
					</h2>
					<div className="mx-auto lg:w-3/4">
						<p className="text-md md:text-lg text-green-800 mt-3 mb-8 text-center leading-relaxed font-inter py-4 px-8 lg:px-4 bg-green-100 rounded-full">
							Our CEO and MD are the driving force behind our innovation and
							success, leading with expertise and a commitment to excellence.
						</p>
					</div>
					<ZeroHundred />
				</div>
				{/* </div> */}

				{/* Connector Line - Mobile */}
			</div>

			{/* Features Section */}
			<div className="bg-white py-10 px-4 sm:px-6 lg:px-8">
				{/* Section Title */}
				<h3 className="text-center text-2xl sm:text-3xl font-playfair font-bold text-green-800 mt-12 mb-6 px-4">
					What makes <span className="text-green-600">KGV Better</span> than the
					rest<span className="text-green-600">?</span>
				</h3>
				<hr className="w-3/4 sm:w-2/4 mx-auto border-t-2 border-green-600 mb-6" />

				{/* Connector Line - Desktop */}
				<div className="hidden md:block relative mx-auto max-w-7xl px-4 md:px-10 pt-10">
					<div className="absolute top-6 left-8 right-8 h-0.5 bg-black z-0"></div>

					<div className="flex justify-between w-full text-sm z-10 mt-[-14px]">
						{[
							'Warranty Provided',
							'No tampering with engine, Gears or Clutch',
							'Dual Fuel Technology',
							'Reduce Air & Noise pollution',
							'Charging as per convenience - Portable Battery',
							'No range anxiety',
						].map((text, idx) => (
							<div
								key={idx}
								className="flex flex-col items-center w-[140px] lg:w-[160px]"
							>
								<div className="w-0.5 h-6 bg-black"></div>
								<div className="bg-green-600 text-white px-3 py-2 rounded-full font-normal text-center w-full h-[70px] lg:h-[80px] flex items-center justify-center text-xs lg:text-sm leading-tight">
									{text}
								</div>
							</div>
						))}
					</div>
				</div>

				{/* Connector Line - Mobile */}
				<div className="md:hidden grid grid-cols-2 gap-4 px-4 max-w-2xl mx-auto">
					{[
						'Warranty Provided',
						'No tampering with engine, Gears or Clutch',
						'Dual Fuel Technology',
						'Reduce Air & Noise pollution',
						'Charging as per convenience - Portable Battery',
						'No range anxiety',
					].map((text, idx) => (
						<div key={idx} className="flex flex-col items-center">
							<div className="bg-green-600 text-white px-3 py-3 rounded-full font-normal text-center w-full min-h-[80px] flex items-center justify-center text-xs leading-tight">
								{text}
							</div>
						</div>
					))}
				</div>
			</div>

			{/* SDG Goals Section */}
			<div className="bg-gradient-to-r from-purple-800 via-pink-600 to-yellow-400 py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
				<div className="text-white rounded-xl p-4 sm:p-6 md:p-10 max-w-7xl mx-auto">
					<h2 className="text-3xl sm:text-4xl font-bold text-center text-yellow-400 mb-4">
						SDG Goals
					</h2>
					<p className="text-center text-sm sm:text-base md:text-xl max-w-3xl mx-auto mb-8 sm:mb-10">
						At Karishma Global Ventures, we are committed to achieving several
						Sustainable Development Goals (SDGs) set by the United Nations. Our
						primary focus areas include providing affordable and sustainable
						mobility solutions, reducing carbon emissions, promoting renewable
						energy usage, and contributing to economic growth and social equity.
						Through innovation and collaboration, we aim to make a positive
						impact on the environment and society, creating a better and more
						sustainable future for all.
					</p>

					<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 md:gap-6 justify-center">
						<img src="SDG/3.png" alt="SDG 3 - Good Health and Well-being" />
						<img
							src="SDG/Goal-07.png"
							alt="SDG 7 - Affordable and Clean Energy"
						/>
						<img
							src="SDG/Goal-09.png"
							alt="SDG 9 - Industry, Innovation and Infrastructure"
						/>
						<img
							src="SDG/Goal-11.png"
							alt="SDG 11 - Sustainable Cities and Communities"
						/>
						<img
							src="SDG/Goal-12.png"
							alt="SDG 12 - Responsible Consumption and Production"
						/>
						<img src="SDG/Goal-13.png" alt="SDG 13 - Climate Action" />
						<img src="SDG/Goal-15.png" alt="SDG 15 - Life on Land" />
						<img
							src="SDG/Goal-17.png"
							alt="SDG 17 - Partnerships for the Goals"
						/>
					</div>
				</div>
			</div>
		</main>
	);
}
